import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendIcon from "@mui/icons-material/Send";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ChatIcon from "@mui/icons-material/Chat";
import ImageIcon from "@mui/icons-material/Image";
import MovieIcon from "@mui/icons-material/Movie";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { chat } from "../../services/api";
import Message from "./Message";
import { avatarColor, initials } from "./avatarUtils";

function ChatPanel({ phone, name, onBack, onConversationUpdate }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingPreview, setPendingPreview] = useState(null);
  const messagesEndRef = useRef(null);
  const lastIdRef = useRef(0);
  const isPollingRef = useRef(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  const scrollToBottom = (smooth = true) => {
    messagesEndRef.current?.scrollIntoView({ behavior: smooth ? "smooth" : "auto" });
  };

  const loadMessages = useCallback(async () => {
    if (!phone) return;
    try {
      const data = await chat.getMessages(phone);
      setMessages(data.messages || []);
      if (data.messages?.length > 0) {
        lastIdRef.current = data.messages[data.messages.length - 1].id;
      }
      setTimeout(() => scrollToBottom(false), 50);
    } catch (err) {
      console.error("Failed to load messages:", err);
    }
  }, [phone]);

  useEffect(() => {
    if (phone) {
      setMessages([]);
      lastIdRef.current = 0;
      loadMessages();
    }
  }, [phone, loadMessages]);

  const playNotification = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 800;
      gain.gain.value = 0.08;
      osc.start();
      osc.stop(ctx.currentTime + 0.12);
    } catch (e) {}
  };

  const pollNewMessages = useCallback(async () => {
    if (!phone || isPollingRef.current) return;
    isPollingRef.current = true;
    try {
      const data = await chat.getNewMessages(phone, lastIdRef.current);
      if (data.messages?.length > 0) {
        let appended = false;
        setMessages((prev) => {
          const existingIds = new Set(prev.map((m) => m.id));
          const newMsgs = data.messages.filter((m) => !existingIds.has(m.id));
          if (newMsgs.length === 0) return prev;
          const updated = [...prev, ...newMsgs];
          lastIdRef.current = updated[updated.length - 1].id;
          appended = true;
          return updated;
        });
        if (appended) {
          setTimeout(() => scrollToBottom(true), 50);
          if (data.messages.some((m) => m.direction === "incoming")) {
            playNotification();
          }
        }
      }
    } catch (err) {}
    isPollingRef.current = false;
  }, [phone]);

  useEffect(() => {
    if (!phone) return;
    const interval = setInterval(pollNewMessages, 3000);
    return () => clearInterval(interval);
  }, [phone, pollNewMessages]);

  const handleSend = async () => {
    if (!phone) return;

    if (pendingFile) {
      setSending(true);
      try {
        await chat.sendMedia(phone, pendingFile, input.trim());
        setInput("");
        clearPendingFile();
        await pollNewMessages();
        onConversationUpdate();
      } catch (err) {
        alert("Failed to send file");
      }
      setSending(false);
      return;
    }

    const text = input.trim();
    if (!text) return;

    setSending(true);
    setInput("");
    try {
      await chat.sendMessage(phone, text);
      await pollNewMessages();
      onConversationUpdate();
    } catch (err) {
      alert("Failed to send");
      setInput(text);
    }
    setSending(false);
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const clearPendingFile = () => {
    if (pendingPreview) URL.revokeObjectURL(pendingPreview);
    setPendingPreview(null);
    setPendingFile(null);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (pendingPreview) URL.revokeObjectURL(pendingPreview);
      setPendingFile(file);
      if (file.type.startsWith("image/")) {
        setPendingPreview(URL.createObjectURL(file));
      } else {
        setPendingPreview(null);
      }
    }
    e.target.value = "";
  };

  useEffect(() => {
    return () => {
      if (pendingPreview) URL.revokeObjectURL(pendingPreview);
    };
  }, [pendingPreview]);

  const windowStatus = useMemo(() => {
    if (!messages.length) return null;
    const lastIncoming = [...messages].reverse().find((m) => m.direction === "incoming");
    if (!lastIncoming) return null;
    const hours = (Date.now() - new Date(lastIncoming.created_at + "Z").getTime()) / 3600000;
    return hours < 24 ? "open" : "closed";
  }, [messages]);

  const groupedMessages = useMemo(() => {
    const groups = [];
    let lastDate = "";
    messages.forEach((msg) => {
      const d = new Date(msg.created_at + "Z");
      if (isNaN(d.getTime())) {
        groups.push({ type: "message", data: msg });
        return;
      }
      const today = new Date();
      const yesterday = new Date();
      yesterday.setDate(today.getDate() - 1);
      let label;
      const isSame = (a, b) =>
        a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
      if (isSame(d, today)) label = "Today";
      else if (isSame(d, yesterday)) label = "Yesterday";
      else label = d.toLocaleDateString([], { weekday: "long", month: "short", day: "numeric", year: d.getFullYear() === today.getFullYear() ? undefined : "numeric" });

      if (label !== lastDate) {
        lastDate = label;
        groups.push({ type: "date", date: label });
      }
      groups.push({ type: "message", data: msg });
    });
    return groups;
  }, [messages]);

  if (!phone) {
    return (
      <div className="chat-panel">
        <div className="chat-empty">
          <div className="chat-empty-icon">
            <ChatIcon sx={{ fontSize: 110 }} />
          </div>
          <h2>CapizFlow Chat</h2>
          <p>
            Select a conversation from the sidebar to start messaging,
            or click the + button to start a new chat.
          </p>
        </div>
      </div>
    );
  }

  const displayName = name || phone;

  return (
    <section className="chat-panel" aria-label="Chat panel">
      <div className="chat-header">
        <span className="back-btn-wrap">
          <Tooltip title="Back to chats">
            <IconButton
              size="small"
              onClick={onBack}
              sx={{ color: "#54656f" }}
              aria-label="Back"
            >
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
        </span>
        <Avatar
          sx={{
            bgcolor: avatarColor(phone || displayName),
            width: 40,
            height: 40,
            fontSize: 16,
            fontWeight: 500,
            color: "#fff",
          }}
        >
          {initials(displayName)}
        </Avatar>
        <div className="chat-header-info">
          <div className="chat-header-name">{displayName}</div>
          <div className="chat-header-status">+{phone}</div>
        </div>
        {windowStatus && (
          <span className={`window-badge window-${windowStatus}`}>
            {windowStatus === "open" ? "24h window open" : "Window closed"}
          </span>
        )}
        <div className="chat-header-actions">
          <Tooltip title="Delete conversation">
            <IconButton
              size="small"
              sx={{ color: "#54656f" }}
              aria-label="Delete conversation"
              onClick={async () => {
                if (window.confirm("Delete this conversation?")) {
                  await chat.deleteConversation(phone);
                  onBack();
                  onConversationUpdate();
                }
              }}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      <div className="messages-container">
        {groupedMessages.map((item, idx) =>
          item.type === "date" ? (
            <div key={`date-${idx}`} className="message-date-separator">
              <span>{item.date}</span>
            </div>
          ) : (
            <Message key={item.data.id} msg={item.data} />
          )
        )}
        <div ref={messagesEndRef} />
      </div>

      {pendingFile && (
        <div className="attach-preview">
          <div className="attach-preview-inner">
            <div className="attach-preview-thumb">
              {pendingPreview ? (
                <img src={pendingPreview} alt="preview" />
              ) : pendingFile.type.startsWith("video/") ? (
                <MovieIcon />
              ) : pendingFile.type.startsWith("image/") ? (
                <ImageIcon />
              ) : (
                <InsertDriveFileIcon />
              )}
            </div>
            <div className="attach-preview-info">
              <div className="attach-preview-name">{pendingFile.name}</div>
              <div className="attach-preview-size">
                {(pendingFile.size / 1024).toFixed(1)} KB
              </div>
            </div>
            <Tooltip title="Remove">
              <IconButton
                size="small"
                onClick={clearPendingFile}
                sx={{ color: "#d9534f" }}
                aria-label="Remove attachment"
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      )}

      <div className="chat-input-area">
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
          onChange={handleFileSelect}
        />
        <Tooltip title="Attach file">
          <IconButton
            onClick={() => fileInputRef.current?.click()}
            sx={{ color: "#54656f" }}
            aria-label="Attach file"
          >
            <AttachFileIcon />
          </IconButton>
        </Tooltip>
        <div className="chat-input-wrapper">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={pendingFile ? "Add a caption..." : "Type a message"}
            rows={1}
          />
        </div>
        <Tooltip title="Send">
          <span>
            <IconButton
              onClick={handleSend}
              disabled={sending || (!input.trim() && !pendingFile)}
              sx={{
                bgcolor: "#075e54",
                color: "#fff",
                width: 42,
                height: 42,
                "&:hover": { bgcolor: "#054d44" },
                "&.Mui-disabled": {
                  bgcolor: "rgba(7,94,84,0.4)",
                  color: "#fff",
                },
              }}
              aria-label="Send message"
            >
              <SendIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </div>
    </section>
  );
}

export default ChatPanel;
